import { Link } from '@cambridgeassessment/cambridge-ui/build/components/Link';
import { Box, Container, Typography } from '@material-ui/core';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import CambridgeOneLogo from 'src/assets/img/CambridgeOneLogo.svg';
import useSchema from 'src/hooks/useSchema';
import useWindowDimensions from 'src/hooks/useWindowDimensions';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import EmailSubscriptionRadio from '../EmailSubscriptionRadio/EmailSubscriptionRadio';
import MyInterestsPreference from '../MyInterestsPreference/MyInterestsPreference';
import { useCommunicationPageStyle } from './styles';

const ANCHORS = {
  myCambridge: {
    title: 'My Cambridge',
    id: 'communications-mycambridge',
  },
  cambridgeOne: {
    title: 'Cambridge One',
    id: 'communications-cambridgeone',
  },
  myInterests: {
    title: 'My Interests',
    id: 'communications-myinterests',
  },
};

const CommunicationsPage: React.FC = () => {
  const MyCambridgeBoxRef = useRef(null);
  const CambridgeOneBoxRef = useRef(null);
  const MyInterestsRef = useRef(null);
  const { width, height } = useWindowDimensions();
  const [offsetTop, setOffsetTop] = useState([]);
  const { subscriptionsSchema } = useSchema();
  const { flags } = useFeatureFlags();
  const showSubjects = flags['mycambridge.subjects'];
  const classes = useCommunicationPageStyle();

  const availableAnchors = useMemo(
    () =>
      Object.values(ANCHORS).filter(
        (anch) => anch.id !== ANCHORS.myInterests.id || showSubjects
      ),
    [showSubjects]
  );

  const handleClick = useCallback(
    (index: number) => {
      window.scrollTo({
        top: offsetTop[index],
        behavior: 'smooth',
      });
    },
    [offsetTop]
  );

  useEffect(() => {
    setOffsetTop([
      MyCambridgeBoxRef?.current?.offsetTop,
      CambridgeOneBoxRef?.current?.offsetTop,
      MyInterestsRef?.current?.offsetTop,
    ]);
  }, [width, height]);

  return (
    <Container disableGutters maxWidth="xl">
      <Box mb={4}>
        <Typography variant="h2">Communications</Typography>
      </Box>
      <Box>
        <Box mb={1}>
          <Typography className={classes.contentSize} variant="h3">
            Contents:
          </Typography>
        </Box>
        {availableAnchors.map(({ title, id }, index) => (
          <Box mb={1} key={id}>
            <Link
              className={classes.fontColor}
              onClick={() => handleClick(index)}
              offset="10"
              underline="none"
              data-testid={`anchor-link-${index}`}
            >
              {title}
            </Link>
          </Box>
        ))}
      </Box>

      <Box mt={4} id={ANCHORS.myCambridge.id}>
        <div ref={MyCambridgeBoxRef} />
        <EmailSubscriptionRadio
          headTitle="My Cambridge"
          bodyTitle="Account emails"
          bodySubtitle="Emails about new activity in your account"
          gigyaSubscriptionField="myCambridge"
        />
      </Box>

      <Box mt={4} id={ANCHORS.cambridgeOne.id}>
        <div ref={CambridgeOneBoxRef} />
        <EmailSubscriptionRadio
          logo={<CambridgeOneLogo />}
          headTitle="Cambridge One"
          headSubtitle="Get access to activities, resources and tools to support your teaching and learning with Cambridge"
          bodyTitle="Update emails"
          bodySubtitle="Sign up for email updates from Cambridge One"
          gigyaSubscriptionField="nemo"
        />
      </Box>

      {showSubjects && (
        <Box mt={4} id={ANCHORS.myInterests.id}>
          <div ref={MyInterestsRef} />
          <MyInterestsPreference subscriptionsSchema={subscriptionsSchema} />
        </Box>
      )}
    </Container>
  );
};

export default memo(CommunicationsPage);
